import { ChevronRight, ExpandMore, UnfoldLess } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import useDebouncedValue from "store/hooks/useDebouncedValue";

// React port of scripts/demo_data/viewer.py — explorable field tree +
// per-field detail panel for a demo-data profile.json.

const SOURCE_COLORS = {
  raw: { bg: "rgba(37,99,235,0.10)", fg: "#2563eb" },
  resolved: { bg: "rgba(21,128,61,0.10)", fg: "#15803d" },
  both: { bg: "rgba(124,58,237,0.10)", fg: "#7c3aed" },
};

const MONO = "ui-monospace, 'SF Mono', Menlo, Consolas, monospace";

function splitPath(p) {
  return p.split(".").filter(Boolean);
}

function buildTree(fields) {
  const root = {
    name: "(root)",
    fullPath: "",
    field: null,
    children: {},
    descendantCount: 0,
  };
  for (const f of fields) {
    const segs = splitPath(f.path);
    let node = root;
    let prefix = "";
    segs.forEach((seg, i) => {
      prefix = prefix ? `${prefix}.${seg}` : seg;
      if (!node.children[seg]) {
        node.children[seg] = {
          name: seg,
          fullPath: prefix,
          field: null,
          children: {},
          descendantCount: 0,
        };
      }
      node = node.children[seg];
      if (i === segs.length - 1) node.field = f;
    });
  }
  const count = (n) => {
    let c = 0;
    for (const child of Object.values(n.children)) {
      c += 1 + count(child);
    }
    n.descendantCount = c;
    return c;
  };
  count(root);
  return root;
}

function defaultExpanded(root) {
  const out = new Set();
  const walk = (node, depth) => {
    if (depth >= 2) return;
    for (const child of Object.values(node.children)) {
      if (Object.keys(child.children).length > 0) {
        out.add(child.fullPath);
        walk(child, depth + 1);
      }
    }
  };
  walk(root, 0);
  return out;
}

function fieldMatchesFilters(f, filters) {
  if (!f) return false;
  const anySrc = filters.raw || filters.resolved || filters.both;
  if (anySrc) {
    const s = f.source || "";
    const ok =
      (filters.raw && s === "raw") ||
      (filters.resolved && s === "resolved") ||
      (filters.both && s === "both");
    if (!ok) return false;
  }
  if (filters.hasExamples && !f.examples?.length) return false;
  if (filters.hasEnum && !Object.keys(f.enum_values || {}).length) return false;
  return true;
}

function nodeMatchesSearch(node, q) {
  if (!q) return true;
  if (node.fullPath.toLowerCase().includes(q)) return true;
  const f = node.field;
  if (!f) return false;
  if ((f.description || "").toLowerCase().includes(q)) return true;
  if ((f.examples || []).some((e) => String(e).toLowerCase().includes(q))) {
    return true;
  }
  return Object.keys(f.enum_values || {}).some((k) =>
    String(k).toLowerCase().includes(q)
  );
}

function computeKeepMap(root, search, filters) {
  const filtersActive =
    !!search ||
    filters.hasExamples ||
    filters.hasEnum ||
    filters.raw ||
    filters.resolved ||
    filters.both;
  const keep = new Map();
  const anyFieldFilter =
    filters.hasExamples ||
    filters.hasEnum ||
    filters.raw ||
    filters.resolved ||
    filters.both;
  const visit = (node) => {
    let selfOk = true;
    if (filtersActive) {
      selfOk = nodeMatchesSearch(node, search);
      if (selfOk && anyFieldFilter) {
        selfOk = node.field ? fieldMatchesFilters(node.field, filters) : false;
      }
    }
    let any = selfOk;
    for (const child of Object.values(node.children)) {
      if (visit(child)) any = true;
    }
    keep.set(node.fullPath, any);
    return any;
  };
  visit(root);
  return { keep, filtersActive };
}

function findNode(root, path) {
  let node = root;
  for (const seg of splitPath(path)) {
    node = node.children[seg];
    if (!node) return null;
  }
  return node;
}

function Highlight({ text, query }) {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query);
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <Box component={"mark"} sx={{ bgcolor: "#fff3a3", borderRadius: 0.5 }}>
        {text.slice(idx, idx + query.length)}
      </Box>
      {text.slice(idx + query.length)}
    </>
  );
}

function SmallChip({ label, colors }) {
  return (
    <Box
      component={"span"}
      sx={{
        px: 0.75,
        borderRadius: 999,
        fontSize: 10,
        fontWeight: 500,
        bgcolor: colors?.bg || "grey.100",
        color: colors?.fg || "text.secondary",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </Box>
  );
}

function PresenceBar({ field }) {
  if (typeof field?.presence_rate !== "number") return null;
  const pct = Math.round(field.presence_rate * 100);
  return (
    <Box
      title={`${pct}% present`}
      sx={{
        width: 36,
        height: 4,
        bgcolor: "grey.300",
        borderRadius: 2,
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: "primary.main" }} />
    </Box>
  );
}

function TreeNode({
  node,
  depth,
  keepInfo,
  expanded,
  selectedPath,
  search,
  onToggle,
  onSelect,
}) {
  const { keep, filtersActive } = keepInfo;
  if (filtersActive && !keep.get(node.fullPath)) return null;
  const children = Object.values(node.children).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
  const hasChildren = children.length > 0;
  const isOpen = expanded.has(node.fullPath);
  const f = node.field;
  return (
    <Box>
      <Stack
        direction={"row"}
        alignItems={"center"}
        spacing={0.5}
        onClick={() => onSelect(node.fullPath)}
        sx={{
          pl: depth * 1.5,
          pr: 0.5,
          py: 0.1,
          borderRadius: 1,
          cursor: "pointer",
          bgcolor:
            node.fullPath === selectedPath ? "action.selected" : undefined,
          "&:hover": { bgcolor: "action.hover" },
          whiteSpace: "nowrap",
        }}
      >
        <Box
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) onToggle(node.fullPath);
          }}
          sx={{
            width: 18,
            display: "inline-flex",
            color: "text.secondary",
            flexShrink: 0,
          }}
        >
          {hasChildren ? (
            isOpen ? (
              <ExpandMore fontSize={"inherit"} />
            ) : (
              <ChevronRight fontSize={"inherit"} />
            )
          ) : (
            <Box sx={{ width: 18, textAlign: "center" }}>{"·"}</Box>
          )}
        </Box>
        <Typography
          component={"span"}
          sx={{ fontFamily: MONO, fontSize: 12, minWidth: 0 }}
        >
          <Highlight text={node.name} query={search} />
        </Typography>
        {f ? (
          <>
            {f.source && (
              <SmallChip label={f.source} colors={SOURCE_COLORS[f.source]} />
            )}
            {Object.keys(f.enum_values || {}).length > 0 && (
              <SmallChip
                label={"enum"}
                colors={{ bg: "rgba(180,83,9,0.10)", fg: "#b45309" }}
              />
            )}
            {f.examples?.length > 0 && (
              <SmallChip
                label={"ex"}
                colors={{ bg: "rgba(59,108,242,0.10)", fg: "#3b6cf2" }}
              />
            )}
            <PresenceBar field={f} />
          </>
        ) : (
          hasChildren && (
            <Typography
              component={"span"}
              sx={{ fontSize: 10, color: "text.secondary" }}
            >
              {`${node.descendantCount} nested`}
            </Typography>
          )
        )}
      </Stack>
      {hasChildren && isOpen && (
        <Box sx={{ borderLeft: "1px dashed", borderColor: "divider", ml: 1 }}>
          {children.map((c) => (
            <TreeNode
              key={c.fullPath}
              node={c}
              depth={depth + 1}
              keepInfo={keepInfo}
              expanded={expanded}
              selectedPath={selectedPath}
              search={search}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography
      sx={{
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 0.5,
        color: "text.secondary",
        fontWeight: 600,
        mb: 0.5,
        mt: 2,
      }}
    >
      {children}
    </Typography>
  );
}

function KvTable({ rows }) {
  return (
    <Box component={"table"} sx={{ borderCollapse: "collapse", fontSize: 12 }}>
      <tbody>
        {rows.map(([k, v]) => (
          <tr key={k}>
            <Box
              component={"td"}
              sx={{ color: "text.secondary", pr: 2, verticalAlign: "top" }}
            >
              {k}
            </Box>
            <Box component={"td"} sx={{ fontFamily: MONO }}>
              {String(v)}
            </Box>
          </tr>
        ))}
      </tbody>
    </Box>
  );
}

function DetailTable({ headers, rows }) {
  return (
    <Box
      component={"table"}
      sx={{
        borderCollapse: "collapse",
        fontSize: 12,
        width: "100%",
        maxWidth: "80ch",
        border: "1px solid",
        borderColor: "divider",
        "& th": {
          textAlign: "left",
          bgcolor: "grey.100",
          fontSize: 11,
          textTransform: "uppercase",
          color: "text.secondary",
          px: 1,
          py: 0.5,
        },
        "& td": {
          px: 1,
          py: 0.5,
          borderTop: "1px solid",
          borderColor: "divider",
          fontFamily: MONO,
          wordBreak: "break-word",
        },
      }}
    >
      <thead>
        <tr>
          {headers.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>{rows}</tbody>
    </Box>
  );
}

function DetailPane({ node, onNavigate }) {
  if (!node) {
    return (
      <Typography
        sx={{
          color: "text.secondary",
          fontStyle: "italic",
          textAlign: "center",
          pt: 8,
        }}
      >
        {"Select a field on the left to inspect it."}
      </Typography>
    );
  }
  const f = node.field;
  const children = Object.values(node.children).sort((a, b) =>
    a.name.localeCompare(b.name)
  );
  return (
    <Box>
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: 14,
          fontWeight: 600,
          wordBreak: "break-all",
          mb: 1,
        }}
      >
        {node.fullPath || "(root)"}
      </Typography>

      {!f && (
        <>
          <Typography
            sx={{ fontStyle: "italic", color: "text.secondary", mb: 1 }}
          >
            {children.length > 0
              ? `Intermediate node — ${node.descendantCount} fields below.`
              : "No field metadata recorded for this path."}
          </Typography>
          {children.length > 0 && (
            <>
              <SectionTitle>{"Immediate children"}</SectionTitle>
              <DetailTable
                headers={["Name", "Type", "Presence", "Source"]}
                rows={children.map((c) => (
                  <tr key={c.fullPath}>
                    <td>
                      <Box
                        component={"a"}
                        href={"#"}
                        onClick={(e) => {
                          e.preventDefault();
                          onNavigate(c.fullPath);
                        }}
                        sx={{ color: "primary.main" }}
                      >
                        {c.name}
                      </Box>
                    </td>
                    <td>{c.field?.field_type || ""}</td>
                    <td>
                      {typeof c.field?.presence_rate === "number"
                        ? `${Math.round(c.field.presence_rate * 100)}%`
                        : "—"}
                    </td>
                    <td>{c.field?.source || ""}</td>
                  </tr>
                ))}
              />
            </>
          )}
        </>
      )}

      {f && (
        <>
          <Stack direction={"row"} spacing={0.75} sx={{ flexWrap: "wrap" }}>
            {f.field_type && <Chip label={f.field_type} size={"small"} />}
            {f.source && (
              <SmallChip label={f.source} colors={SOURCE_COLORS[f.source]} />
            )}
            {Object.keys(f.enum_values || {}).length > 0 && (
              <SmallChip
                label={`enum: ${Object.keys(f.enum_values).length}`}
                colors={{ bg: "rgba(180,83,9,0.10)", fg: "#b45309" }}
              />
            )}
            {f.examples?.length > 0 && (
              <SmallChip
                label={`${f.examples.length} examples`}
                colors={{ bg: "rgba(59,108,242,0.10)", fg: "#3b6cf2" }}
              />
            )}
          </Stack>
          {f.description && (
            <Typography
              sx={{
                fontStyle: "italic",
                color: "text.secondary",
                my: 1.5,
                maxWidth: "80ch",
              }}
            >
              {f.description}
            </Typography>
          )}

          <SectionTitle>{"Stats"}</SectionTitle>
          <KvTable
            rows={[
              [
                "presence_rate",
                typeof f.presence_rate === "number"
                  ? `${(f.presence_rate * 100).toFixed(2)}%`
                  : "—",
              ],
              ["observation_count", f.observation_count ?? "—"],
              ["null_count", f.null_count ?? "—"],
              ["distinct_count", f.distinct_count ?? "—"],
            ]}
          />

          {f.examples?.length > 0 && (
            <>
              <SectionTitle>{"Examples"}</SectionTitle>
              <DetailTable
                headers={["#", "Value"]}
                rows={f.examples.map((ex, i) => (
                  <tr key={i}>
                    <td style={{ width: 32 }}>{i + 1}</td>
                    <td>{typeof ex === "string" ? ex : JSON.stringify(ex)}</td>
                  </tr>
                ))}
              />
            </>
          )}

          {Object.keys(f.enum_values || {}).length > 0 &&
            (() => {
              const entries = Object.entries(f.enum_values).sort(
                (a, b) => (b[1] || 0) - (a[1] || 0)
              );
              const total = entries.reduce(
                (a, [, v]) => a + (Number(v) || 0),
                0
              );
              return (
                <>
                  <SectionTitle>{"Enum values"}</SectionTitle>
                  <DetailTable
                    headers={["Key", "Count", "%"]}
                    rows={entries.map(([k, v]) => (
                      <tr key={k}>
                        <td>{String(k)}</td>
                        <td style={{ width: 80 }}>{String(v)}</td>
                        <td style={{ width: 80 }}>
                          {total ? `${((v / total) * 100).toFixed(1)}%` : "—"}
                        </td>
                      </tr>
                    ))}
                  />
                </>
              );
            })()}

          {["string", "numeric", "date", "array", "id_map", "ratio"]
            .filter((key) => f[key])
            .map((key) => (
              <Box key={key}>
                <SectionTitle>{`${key} stats`}</SectionTitle>
                <KvTable rows={Object.entries(f[key])} />
              </Box>
            ))}

          <SectionTitle>{"Raw FieldProfile JSON"}</SectionTitle>
          <Box
            component={"pre"}
            sx={{
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              p: 1.5,
              maxHeight: 320,
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontSize: 12,
              fontFamily: MONO,
            }}
          >
            {JSON.stringify(f, null, 2)}
          </Box>
        </>
      )}
    </Box>
  );
}

const FILTER_DEFS = [
  ["hasExamples", "has examples"],
  ["hasEnum", "enum"],
  ["raw", "raw"],
  ["resolved", "resolved"],
  ["both", "both"],
];

export default function DemoDataProfileViewer({ profile }) {
  const fields = useMemo(() => profile?.fields || [], [profile]);
  const tree = useMemo(() => buildTree(fields), [fields]);
  const [userExpanded, setUserExpanded] = useState(null);
  const [selectedPath, setSelectedPath] = useState(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    hasExamples: false,
    hasEnum: false,
    raw: false,
    resolved: false,
    both: false,
  });

  // Filtering scans every field, which is slow on big profiles; wait for a typing pause
  const query = useDebouncedValue(search).trim().toLowerCase();
  const baseExpanded = useMemo(() => userExpanded ?? defaultExpanded(tree), [
    userExpanded,
    tree,
  ]);
  // Searching auto-expands all ancestors of matching fields
  const expanded = useMemo(() => {
    if (!query) return baseExpanded;
    const out = new Set(baseExpanded);
    for (const f of fields) {
      const text = [
        f.path,
        f.description || "",
        ...(f.examples || []).map(String),
        ...Object.keys(f.enum_values || {}),
      ]
        .join(" ")
        .toLowerCase();
      if (text.includes(query)) {
        let acc = "";
        for (const seg of splitPath(f.path)) {
          acc = acc ? `${acc}.${seg}` : seg;
          out.add(acc);
        }
      }
    }
    return out;
  }, [baseExpanded, fields, query]);

  const keepInfo = useMemo(() => computeKeepMap(tree, query, filters), [
    tree,
    query,
    filters,
  ]);

  const visibleFieldCount = useMemo(() => {
    if (!keepInfo.filtersActive) return fields.length;
    let n = 0;
    const walk = (node) => {
      if (!keepInfo.keep.get(node.fullPath)) return;
      if (node.field) n++;
      Object.values(node.children).forEach(walk);
    };
    Object.values(tree.children).forEach(walk);
    return n;
  }, [keepInfo, tree, fields.length]);

  const handleToggle = (path) => {
    const next = new Set(expanded);
    if (next.has(path)) next.delete(path);
    else next.add(path);
    setUserExpanded(next);
  };

  const handleNavigate = (path) => {
    const next = new Set(expanded);
    let acc = "";
    for (const seg of splitPath(path)) {
      acc = acc ? `${acc}.${seg}` : seg;
      next.add(acc);
    }
    setUserExpanded(next);
    setSelectedPath(path);
  };

  const selectedNode = selectedPath ? findNode(tree, selectedPath) : null;
  const rootChildren = Object.values(tree.children).sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  return (
    <Stack sx={{ height: "100%", minHeight: 0 }}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        spacing={1}
        sx={{
          p: 1,
          borderBottom: 1,
          borderColor: "divider",
          flexWrap: "wrap",
        }}
      >
        <TextField
          size={"small"}
          type={"search"}
          placeholder={"search field path / description / example …"}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 320 }}
        />
        {FILTER_DEFS.map(([key, label]) => (
          <FormControlLabel
            key={key}
            control={
              <Checkbox
                size={"small"}
                checked={filters[key]}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, [key]: e.target.checked }))
                }
              />
            }
            label={label}
            sx={{
              "& .MuiFormControlLabel-label": {
                fontSize: 12,
                color: "text.secondary",
              },
            }}
          />
        ))}
        <Box
          onClick={() => setUserExpanded(new Set())}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            fontSize: 12,
            color: "text.secondary",
            cursor: "pointer",
          }}
        >
          <UnfoldLess fontSize={"inherit"} />
          {"collapse all"}
        </Box>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {keepInfo.filtersActive
            ? `${visibleFieldCount} / ${fields.length} fields`
            : `${fields.length} fields`}
        </Typography>
      </Stack>
      <Stack direction={"row"} sx={{ flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            width: "38%",
            minWidth: 340,
            overflow: "auto",
            borderRight: 1,
            borderColor: "divider",
            p: 1,
          }}
        >
          {rootChildren.map((c) => (
            <TreeNode
              key={c.fullPath}
              node={c}
              depth={0}
              keepInfo={keepInfo}
              expanded={expanded}
              selectedPath={selectedPath}
              search={query}
              onToggle={handleToggle}
              onSelect={setSelectedPath}
            />
          ))}
        </Box>
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          <DetailPane node={selectedNode} onNavigate={handleNavigate} />
        </Box>
      </Stack>
    </Stack>
  );
}
